import { useState, useMemo, useCallback } from 'react'
import { addDays, format, startOfWeek } from 'date-fns'
import { es } from 'date-fns/locale'
import React from 'react'
import {
  TreePine,
  Building2,
  Leaf,
  Activity,
  Mountain,
  Flower2,
} from 'lucide-react'
import { useAvailability } from '../contexts/AvailabilityContext'
import { useAdminData } from '../contexts/AdminDataContext'
import { publicApi } from '../admin/api/client'

export interface TimeSlot {
  id: string
  time: string
  spots: number
  available: number
  price?: number
  sessionId?: string
}

// Alias used by BookingSection
export type Timeslot = TimeSlot

export interface Location {
  id: number | string
  name: string
  address: string
  icon: React.ReactNode
}

const LOCATION_ICONS = [
  React.createElement(TreePine, { size: 24 }),
  React.createElement(Building2, { size: 24 }),
  React.createElement(Leaf, { size: 24 }),
  React.createElement(Activity, { size: 24 }),
  React.createElement(Mountain, { size: 24 }),
  React.createElement(Flower2, { size: 24 }),
]

function formatTime(time24: string): string {
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hour12 = h % 12 || 12
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`
}

function formatDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function useBookingData(activeTab: 'presencial' | 'virtual') {
  const { getAvailableDates, getSlotsForDate } = useAvailability()
  const {
    locations: adminLocations,
    sessions: adminSessions,
    classTypes: adminClassTypes,
    saveBooking,
  } = useAdminData()

  const [timeslots, setTimeslots] = useState<Timeslot[]>([])
  const [isApiConnected, setIsApiConnected] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loadingInitial, setLoadingInitial] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Derive locations directly from admin context — always reactive
  const locations: Location[] = useMemo(() => {
    return adminLocations
      .filter((l) => l.is_active)
      .map((l, i) => ({
        id: l.id,
        name: l.name,
        address: l.address || '',
        icon: LOCATION_ICONS[i % LOCATION_ICONS.length],
      }))
  }, [adminLocations])

  // Derive available dates directly from admin sessions + availability context — always reactive
  const availableDates: Set<string> | null = useMemo(() => {
    const dates = new Set<string>()

    // Add dates from admin sessions that match the active tab
    adminSessions.forEach((s) => {
      const ct = adminClassTypes.find((c) => c.id === s.class_type_id)
      if (ct && ct.modality === activeTab && s.status !== 'cancelled') {
        dates.add(s.session_date)
      }
    })

    // Also add dates from AvailabilityContext
    const contextDates = getAvailableDates(activeTab)
    contextDates.forEach((d) => dates.add(d))

    return dates.size > 0 ? dates : null
  }, [adminSessions, adminClassTypes, activeTab, getAvailableDates])

  // When a date is selected, build timeslots from admin data
  const fetchTimeslots = useCallback(
    async (date: Date) => {
      setLoadingSessions(true)
      setTimeslots([])

      const dateKey = formatDateKey(date)

      // Try API first (will fail if no backend)
      try {
        const data = await publicApi.getSessions({
          month: date.getMonth() + 1,
          year: date.getFullYear(),
          modality: activeTab,
        })

        const filtered = data.sessions.filter((s: any) => {
          const sDate = s.session_date?.split('T')[0] || s.session_date
          return sDate === dateKey
        })

        const mapped: Timeslot[] = filtered.map((s: any) => ({
          id: s.id,
          time: formatTime(s.start_time),
          spots: s.capacity,
          available: s.spots_available ?? s.capacity,
          price: s.price,
          sessionId: s.id,
        }))

        setTimeslots(mapped)
        setIsApiConnected(true)
        setLoadingSessions(false)
        return
      } catch {
        setIsApiConnected(false)
      }

      // Fallback: use admin sessions from context
      const daySessions = adminSessions.filter((s) => {
        const ct = adminClassTypes.find((c) => c.id === s.class_type_id)
        return (
          s.session_date === dateKey &&
          ct &&
          ct.modality === activeTab &&
          s.status !== 'cancelled'
        )
      })

      if (daySessions.length > 0) {
        setTimeslots(
          daySessions.map((s) => {
            const ct = adminClassTypes.find((c) => c.id === s.class_type_id)
            return {
              id: s.id,
              time: formatTime(s.start_time),
              spots: s.capacity,
              available: s.spots_available,
              price: ct?.price || 1,
              sessionId: s.id,
            }
          }),
        )
      } else {
        // Fallback to AvailabilityContext if no explicit sessions
        const contextSlots = getSlotsForDate(dateKey, activeTab)
        if (contextSlots.length > 0) {
          setTimeslots(
            contextSlots.map((s) => ({
              id: s.id,
              time: s.time,
              spots: s.capacity,
              available: s.capacity,
              price: s.price,
              sessionId: s.id,
            })),
          )
        }
      }

      setLoadingSessions(false)
    },
    [activeTab, getSlotsForDate, adminSessions, adminClassTypes],
  )

  // Create payment / booking
  const createPayment = useCallback(
    async (params: {
      sessionId?: string
      studentName: string
      studentEmail: string
      studentPhone?: string
      level?: string
    }) => {
      if (!isApiConnected || !params.sessionId) {
        const session = adminSessions.find((s) => s.id === params.sessionId)
        const ct = session
          ? adminClassTypes.find((c) => c.id === session.class_type_id)
          : null

        saveBooking({
          student_name: params.studentName,
          student_email: params.studentEmail,
          class_name:
            session?.class_name ||
            (activeTab === 'presencial' ? 'Yoga Presencial' : 'Yoga Virtual'),
          session_date: session?.session_date || formatDateKey(new Date()),
          start_time: session?.start_time || '10:00',
          amount_paid: ct?.price || 1,
          payment_status: 'paid',
          booking_status: 'confirmed',
        })

        return { success: true, mock: true }
      }

      try {
        const result = await publicApi.createPaymentIntent({
          sessionId: params.sessionId,
          studentName: params.studentName,
          studentEmail: params.studentEmail,
          studentPhone: params.studentPhone,
          level: params.level,
        })
        return { success: true, mock: false, ...result }
      } catch {
        return { success: true, mock: true }
      }
    },
    [isApiConnected, adminSessions, adminClassTypes, activeTab, saveBooking],
  )

  return {
    locations,
    timeslots,
    availableDates,
    isApiConnected,
    loadingSessions,
    loadingInitial,
    error,
    fetchTimeslots,
    createPayment,
    fallbackTimeslots: {
      presencial: [
        {
          id: 'fb-p1',
          time: '8:00 AM',
          spots: 15,
          available: 8,
          price: 1,
          sessionId: 'fb-p1',
        },
        {
          id: 'fb-p2',
          time: '10:00 AM',
          spots: 15,
          available: 5,
          price: 1,
          sessionId: 'fb-p2',
        },
        {
          id: 'fb-p3',
          time: '5:00 PM',
          spots: 15,
          available: 12,
          price: 1,
          sessionId: 'fb-p3',
        },
      ],
      virtual: [
        {
          id: 'fb-v1',
          time: '9:00 AM',
          spots: 50,
          available: 30,
          price: 1,
          sessionId: 'fb-v1',
        },
        {
          id: 'fb-v2',
          time: '7:00 PM',
          spots: 50,
          available: 40,
          price: 1,
          sessionId: 'fb-v2',
        },
      ],
    } as Record<string, Timeslot[]>,
  }
}
